import { useEffect, useRef } from "react";
import { toast } from "sonner";
import { api } from "../../lib/api";
import { MessageCircle } from "lucide-react";

/**
 * Polling silencioso do endpoint /admin/analytics/live pra detectar novos
 * cliques em botões WhatsApp. Toca um beep + toast piscante pra alertar
 * o admin — assim ele consegue retornar enquanto a família ainda tá online.
 *
 * - Roda no `/admin` (montado uma vez no AdminDashboard).
 * - Guarda o último timestamp visto em localStorage — sobrevive a reloads.
 * - Só toca som se a aba tá visível (ou usa Notification API se possível).
 * - Ignora os primeiros dados da montagem (só notifica cliques novos).
 */
const LS_KEY = "raf.admin.wa_last_seen";
const POLL_MS = 15000;

// Beep sintético via WebAudio — leve, sem asset externo. 3 tons rápidos
// pra ficar reconhecível (não é o som padrão do WhatsApp).
const playBeep = () => {
  try {
    const AC = window.AudioContext || window.webkitAudioContext;
    if (!AC) return;
    const ctx = new AC();
    const now = ctx.currentTime;
    [880, 1175, 1568].forEach((freq, i) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = "sine";
      osc.frequency.value = freq;
      gain.gain.setValueAtTime(0, now + i * 0.15);
      gain.gain.linearRampToValueAtTime(0.15, now + i * 0.15 + 0.02);
      gain.gain.linearRampToValueAtTime(0, now + i * 0.15 + 0.12);
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.start(now + i * 0.15);
      osc.stop(now + i * 0.15 + 0.15);
    });
    setTimeout(() => ctx.close(), 800);
  } catch { /* ignore */ }
};

export const WhatsAppLiveNotifier = () => {
  const initialized = useRef(false);
  const lastSeenRef = useRef(null);

  useEffect(() => {
    try {
      lastSeenRef.current = localStorage.getItem(LS_KEY);
    } catch { /* ignore */ }

    let cancelled = false;
    const check = async () => {
      try {
        const { data } = await api.get("/admin/analytics/live");
        if (cancelled) return;
        const clicks = data.whatsapp_clicks_24h || [];
        if (clicks.length === 0) return;
        const latest = clicks[0]; // já vem sorted desc por `at`
        const latestAt = latest.at;
        // Primeira carga — só marca o baseline, não alerta
        if (!initialized.current) {
          initialized.current = true;
          lastSeenRef.current = latestAt;
          try { localStorage.setItem(LS_KEY, latestAt); } catch { /* ignore */ }
          return;
        }
        if (!lastSeenRef.current || latestAt > lastSeenRef.current) {
          // Novos cliques desde a última leitura
          const newOnes = clicks.filter((c) => c.at > (lastSeenRef.current || ""));
          lastSeenRef.current = latestAt;
          try { localStorage.setItem(LS_KEY, latestAt); } catch { /* ignore */ }
          if (newOnes.length > 0) {
            playBeep();
            const first = newOnes[0];
            const extra = newOnes.length > 1 ? ` +${newOnes.length - 1}` : "";
            toast(`Nova conversa WhatsApp${extra}`, {
              description: `${first.cta} · ${first.path} · via ${first.referrer}`,
              icon: <MessageCircle className="w-5 h-5 text-emerald-600" />,
              duration: 15000,
              className: "border-l-4 border-emerald-500",
            });
            // Notificação nativa do navegador (se permitido)
            if ("Notification" in window && Notification.permission === "granted") {
              try {
                new Notification("RAF Direct · Clique WhatsApp", {
                  body: `${first.cta} em ${first.path}`,
                  tag: "raf-wa-click",
                });
              } catch { /* ignore */ }
            }
          }
        }
      } catch { /* silent */ }
    };

    // Pede permissão de notificação nativa uma vez (não obrigatório)
    if ("Notification" in window && Notification.permission === "default") {
      Notification.requestPermission().catch(() => {});
    }

    check();
    const id = setInterval(check, POLL_MS);
    return () => { cancelled = true; clearInterval(id); };
  }, []);

  return null; // headless
};

export default WhatsAppLiveNotifier;
